import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api.js";
import { URLS } from "../utils/urls.js";
import { useTopLoader } from "../context/TopLoaderContext";

const PlayList = () => {
  const [loading, setLoading] = useState(true);
  const [songs, setSongs] = useState([]);
  const [selected, setSelected] = useState([]);
  const { start, stop } = useTopLoader();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSongs();
  }, []);

  const fetchSongs = async () => {
    start();
    try {
      const res = await api.get(URLS.GET_SONG_LIST);
      setSongs(res.data || []);
      setSelected([]);
    } catch (err) {
      console.error("Error loading songs:", err);
    } finally {
      setLoading(false);
      stop();
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selected.length === songs.length) {
      setSelected([]);
    } else {
      setSelected(songs.map((s) => s.id));
    }
  };

  const deleteSelected = async () => {
    if (!selected.length) return;

    if (!window.confirm(`Delete ${selected.length} song(s)?`)) return;

    try {
      await api.delete(URLS.DELETE_SONGS, {
        data: { ids: selected }
      });
      fetchSongs();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete songs");
    }
  };

  if (loading) return <div className="p-6 text-center">Loading...</div>;

  return (
    <div className="p-6 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold">Playlist Songs</h1>

        <div className="flex gap-3">
          {selected.length > 0 && (
            <button
              onClick={deleteSelected}
              className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 transition"
            >
              Delete ({selected.length})
            </button>
          )}

          <button
            className="px-4 py-2 bg-[#db8654] text-white rounded-md text-sm hover:bg-[#c77445] transition"
            onClick={() => navigate("/admin/playlist/create")}
          >
            + Add Song
          </button>
        </div>
      </div>

      <div className="shadow-md rounded-lg overflow-hidden border border-gray-200 bg-white">
        <table className="min-w-full text-left">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="p-3">
                <input
                  type="checkbox"
                  checked={songs.length > 0 && selected.length === songs.length}
                  onChange={toggleSelectAll}
                />
              </th>
              <th className="p-3 font-medium text-gray-600">ID</th>
              <th className="p-3 font-medium text-gray-600">Song</th>
              <th className="p-3 font-medium text-gray-600">Artist</th>
              <th className="p-3 font-medium text-gray-600">Platform</th>
              <th className="p-3 font-medium text-gray-600">Vibe</th>
              <th className="p-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>

          <tbody>
            {songs.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-6 text-center text-gray-500 italic">
                  No Songs Found
                </td>
              </tr>
            ) : (
              songs.map((s) => (
                <tr
                  key={s.id}
                  className={`border-b transition ${
                    selected.includes(s.id) ? "bg-gray-50" : "hover:bg-gray-50"
                  }`}
                >
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selected.includes(s.id)}
                      onChange={() => toggleSelect(s.id)}
                    />
                  </td>

                  <td className="p-3">{s.id}</td>
                  <td className="p-3 font-medium">{s.title}</td>
                  <td className="p-3">{s.artist}</td>

                  <td className="p-3 capitalize">
                    <span className="px-2 py-1 text-xs rounded-full bg-gray-100 border">
                      {s.platform.replace("_", " ")}
                    </span>
                  </td>

                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {s.personality_codes?.map(code => (
                        <span key={code} className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-bold rounded uppercase">
                          {code}
                        </span>
                      ))}
                    </div>
                  </td>

                  <td className="p-3 space-x-3">
                    <a
                      href={s.song_url}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition"
                    >
                      Open
                    </a>

                    <button
                      className="px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition"
                      onClick={() =>
                        navigate(`/admin/playlist/edit/${s.id}`)
                      }
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PlayList;
